import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { formatCurrency, formatCurrencyShort } from '../lib/format.js';

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  const isIn = entry.payload.kind === 'in';
  return (
    <div className="bg-white rounded-lg shadow-lg border border-slate-200 px-3 py-2 text-xs min-w-[180px]">
      <p className="font-semibold text-slate-700 mb-1 truncate max-w-[200px]">{label}</p>
      <p className={`font-medium tabular-nums ${isIn ? 'text-green-700' : 'text-red-600'}`}>
        {isIn ? '+' : '-'}{formatCurrency(Math.abs(entry.value))}
      </p>
      <p className="text-slate-400">{entry.payload.count} mouvement{entry.payload.count > 1 ? 's' : ''}</p>
    </div>
  );
}

/**
 * Répartition des mouvements par catégorie (entrées en vert, sorties en rouge).
 *
 * Props :
 *   byCategory — [{ category_id, category_name, kind:'in'|'out', total_cents, count }]
 */
export default function PettyCashCategoryBreakdown({ byCategory = [] }) {
  if (!byCategory.length) {
    return (
      <div className="flex items-center justify-center text-sm text-slate-400 py-8">
        Aucune donnée sur la période
      </div>
    );
  }

  // On sépare entrées et sorties, puis on les trie par montant décroissant
  const inItems = byCategory
    .filter((c) => c.kind === 'in')
    .sort((a, b) => b.total_cents - a.total_cents);
  const outItems = byCategory
    .filter((c) => c.kind === 'out')
    .sort((a, b) => b.total_cents - a.total_cents);

  // Toutes les catégories triées : sorties d'abord (plus impactantes visuellement)
  const chartData = [
    ...outItems.map((c) => ({
      name: c.category_name ?? 'Sans catégorie',
      value: -c.total_cents, // négatif pour sorties
      abs: c.total_cents,
      kind: 'out',
      count: c.count,
    })),
    ...inItems.map((c) => ({
      name: c.category_name ?? 'Sans catégorie',
      value: c.total_cents,
      abs: c.total_cents,
      kind: 'in',
      count: c.count,
    })),
  ];

  // Tronque les labels longs (mobile)
  function truncate(str, max = 18) {
    if (!str) return '';
    return str.length > max ? str.slice(0, max - 1) + '…' : str;
  }

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={Math.max(160, chartData.length * 36)}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 4, right: 16, bottom: 4, left: 0 }}
          barCategoryGap="20%"
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#f1f5f9"
            horizontal={false}
          />

          <XAxis
            type="number"
            tickFormatter={(v) => formatCurrencyShort(Math.abs(v))}
            tick={{ fontSize: 10, fill: '#64748b' }}
            tickLine={false}
            axisLine={false}
          />

          <YAxis
            type="category"
            dataKey="name"
            width={110}
            tickFormatter={(v) => truncate(v)}
            tick={{ fontSize: 11, fill: '#475569' }}
            tickLine={false}
            axisLine={false}
          />

          <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f8fafc' }} />

          <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={28}>
            {chartData.map((entry, idx) => (
              <Cell
                key={idx}
                fill={entry.kind === 'in' ? '#16a34a' : '#dc2626'}
                fillOpacity={0.85}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
